import React from 'react'
import { Cell, Pie, PieChart, ResponsiveContainer, Sector } from 'recharts'
import styled from 'styled-components'
import { ISourceDistribution } from '../../../types/statistics.types'
import useWindowWidth from '../../../utils/use.window.width'

const Wrapper = styled.div`
	margin-bottom: 125px;

	svg, path, g {
		outline: none !important;
		focus-visible: none;
	}

	@media (max-width: 1024px) { margin-bottom: 55px; }
	@media (max-width: 768px) { margin-bottom: 30px; }
`

const Title = styled.h2`
	font-family: 'e-Ukraine', sans-serif;
	font-weight: 400;
	font-size: 20px;
	line-height: 114%;
	text-align: justify;
	color: #000;
	margin: 0 0 15px;
	@media (max-width: 530px) { font-size: 15px; margin: 0 0 10px; }
`

const DateRange = styled.p`
	font-family: 'e-Ukraine', sans-serif;
	font-weight: 400;
	font-size: 14px;
	line-height: 114%;
	text-align: justify;
	color: #8f8f8f;
	margin: 0 0 24px;
	@media (max-width: 530px) { font-size: 12px; }
`

const ChartLayout = styled.div`
	display: flex;
	padding-top: 50px;
	justify-content: end;
	max-width: 1200px;
	gap: 140px;
	@media (max-width: 1024px) { gap: 0px; }
	@media (max-width: 768px) { flex-direction: column; }
`

const PieWrapper = styled.div`
	position: relative;
	width: 363px;
	height: 363px;
	flex-shrink: 0;
	@media (max-width: 1024px) { width: 470px; height: 363px; }
`

const FloatingLabel = styled.div<{ $top: string; $left: string; $align?: string }>`
	position: absolute;
	top: ${({ $top }) => $top};
	left: ${({ $left }) => $left};
	width: 100%;
`

const LabelPct = styled.div`
	font-family: 'e-Ukraine', sans-serif;
	font-weight: 400;
	font-size: 32px;
	line-height: 114%;
	text-align: justify;
	color: #189653;
	@media (max-width: 1200px) { font-size: 24px; }
	@media (max-width: 530px) { font-size: 14px; }
`

const LabelCount = styled.span`
	font-family: 'e-Ukraine', sans-serif;
	font-weight: 400;
	font-size: 20px;
	line-height: 114%;
	text-align: justify;
	color: #189653;
	margin-left: 4px;
	@media (max-width: 1200px) { font-size: 18px; }
	@media (max-width: 530px) { font-size: 11px; }
`

const LabelName = styled.div`
	font-family: 'e-Ukraine', sans-serif;
	font-weight: 400;
	font-size: 20px;
	line-height: 114%;
	text-align: justify;
	color: #189653;
	border-left: 3px solid #1a7a4a;
	padding-left: 6px;
	margin-top: 2px;
	@media (max-width: 1200px) { font-size: 14px; }
	@media (max-width: 530px) { font-size: 11px; }
`

const LegendBox = styled.div`
	display: flex;
	flex-direction: column;
	gap: 40px;
	margin-top: 40px;
	margin-left: 32px;
	@media (max-width: 1024px) { margin-left: 0px; margin-top: 85px; gap: 20px; }
	@media (max-width: 530px) { margin-top: 35px; }
`

const LegendItem = styled.div`
	display: flex;
	align-items: center;
	gap: 10px;
	font-size: 13px;
	color: #333;
	@media (max-width: 1200px) { gap: 5px; }
`

const LegendBar = styled.span<{ $color: string }>`
	display: inline-block;
	border-radius: 11px;
	width: 81px;
	height: 16px;
	background: ${({ $color }) => $color};
	flex-shrink: 0;
	@media (max-width: 1200px) { width: 50px; height: 12px; }
	@media (max-width: 530px) { width: 30px; height: 12px; }
`

export const LegendBarValue = styled.div`
	font-family: 'e-Ukraine', sans-serif;
	font-weight: 400;
	font-size: 20px;
	line-height: 114%;
	text-align: justify;
	color: #6b6b6b;
	margin-right: 15px;
	margin-left: 8px;
	@media (max-width: 1200px) { margin-right: 5px; }
`

export const LegendBarItemName = styled(LegendBarValue)`
	font-size: 15px;
	margin-right: 0px;
	margin-left: 0px;
	@media (max-width: 1200px) { font-size: 12px; }
`

const COLORS = ['#189653', '#01b75c', '#127842']

interface Props {
	data: ISourceDistribution
	dateRange: string
}

export const SourceDistributionChartComponent: React.FC<Props> = ({ data, dateRange }) => {
	const chartData = [
		{ name: 'Інші джерела', value: data.other, count: data.totalOther, color: COLORS[0] },
		{ name: 'Веб-портал', value: data.webPortal, count: data.totalWebPortal, color: COLORS[1] },
		{ name: 'Телефонне звернення', value: data.phone, count: data.totalPhone, color: COLORS[2] },
	]

	const windowWidth = useWindowWidth()
	const isMobile = windowWidth <= 530
	const isTablet = windowWidth <= 768
	const isSmallDesktop = windowWidth <= 1024
	const isMediumDesktop = windowWidth <= 1200

	const outerRadius = isTablet ? 110 : isSmallDesktop ? 115 : isMediumDesktop ? 130 : 180

	return (
		<Wrapper>
			<Title>Джерела надходження звернень</Title>
			<DateRange>{dateRange}</DateRange>

			<ChartLayout>
				<PieWrapper>
					<ResponsiveContainer width='100%' height='100%'>
						<PieChart>
							<Pie
								data={chartData}
								cx='50%'
								cy='50%'
								outerRadius={outerRadius}
								dataKey='value'
								startAngle={90}
								endAngle={-270}
								strokeWidth={0}
								activeShape={(props: any) => {
									const { cx, cy, innerRadius, outerRadius, startAngle, endAngle, fill } = props
									return (
										<g style={{ filter: 'drop-shadow(0 4px 12px rgba(0,0,0,0.2))' }}>
											<Sector
												cx={cx}
												cy={cy}
												innerRadius={innerRadius}
												outerRadius={outerRadius + 2}
												startAngle={startAngle}
												endAngle={endAngle}
												fill={fill}
											/>
										</g>
									)
								}}
							>
								{chartData.map((entry, index) => (
									<Cell key={index} fill={entry.color} />
								))}
							</Pie>
						</PieChart>
					</ResponsiveContainer>

					<FloatingLabel $top='30%' $left={isSmallDesktop ? '0' : isMediumDesktop ? '-20%' : '-55%'} $align='right'>
						<div style={{ borderLeft: '3px solid #1a7a4a', paddingLeft: 6 }}>
							<div>
								<LabelPct style={{ display: 'inline' }}>{data.other}%</LabelPct>
								<LabelCount>{data.totalOther.toLocaleString('uk-UA')}</LabelCount>
							</div>
							<LabelName style={{ border: 'none', padding: 0 }}>Інші джерела</LabelName>
						</div>
					</FloatingLabel>

					<FloatingLabel $top='2%' $left={isMobile ? '55%' : isMediumDesktop ? '70%' : '100%'}>
						<div style={{ borderLeft: '3px solid #1a7a4a', paddingLeft: 6 }}>
							<div>
								<LabelPct style={{ display: 'inline' }}>{data.webPortal}%</LabelPct>
								<LabelCount>{data.totalWebPortal.toLocaleString('uk-UA')}</LabelCount>
							</div>
							<LabelName style={{ border: 'none', padding: 0 }}>Веб-портал</LabelName>
						</div>
					</FloatingLabel>

					<FloatingLabel
						$top={isMobile ? '85%' : isMediumDesktop ? '90%' : '100%'}
						$left={isMobile ? '40%' : isMediumDesktop ? '60%' : '104%'}
					>
						<div style={{ borderLeft: '3px solid #1a7a4a', paddingLeft: 6 }}>
							<div>
								<LabelPct style={{ display: 'inline' }}>{data.phone}%</LabelPct>
								<LabelCount>{data.totalPhone.toLocaleString('uk-UA')}</LabelCount>
							</div>
							<LabelName style={{ border: 'none', padding: 0 }}>Телефонне звернення</LabelName>
						</div>
					</FloatingLabel>
				</PieWrapper>

				<LegendBox>
					{chartData.map(item => (
						<LegendItem key={item.name}>
							<LegendBar $color={item.color} />
							<LegendBarValue>{item.value}%</LegendBarValue>
							<LegendBarItemName>{item.count.toLocaleString('uk-UA')}</LegendBarItemName>
							<LegendBarItemName>{item.name}</LegendBarItemName>
						</LegendItem>
					))}
				</LegendBox>
			</ChartLayout>
		</Wrapper>
	)
}